import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit, ListButton, RefreshButton } from '@refinedev/antd';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  UploadFile,
  Button,
  message,
  Space,
} from 'antd';
import { useTranslation } from '@refinedev/core';
import { BookOutlined, UploadOutlined } from '@ant-design/icons';
import { endPoints } from '@/api/configs';
import useAPI, { useGet } from '@/api/useApi';
import { UserData } from '@/types/user';
import { GetOneClass } from '@/api/types/getOneClass';
import dayjs from 'dayjs';
export const EditClass = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { translate } = useTranslation();
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [removedStudents, setRemovedStudents] = useState<
    {
      value: number;
      label: string;
    }[]
  >([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { usePost, usePatch } = useAPI();
  const { data: usersData, isLoading: usersLoading } = useGet<UserData[]>(
    endPoints.availableUsers(),
    endPoints.availableUsers()
  );
  const { data: classData } = useGet<GetOneClass>(
    endPoints.class(id),
    endPoints.class(id)
  );
  const { mutate: uploadImage, isLoading: uploadImageLoading } = usePost<
    { id: string },
    FormData
  >(endPoints.upload(), {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  const { mutate: updateClass, isLoading: updateClassLoading } = usePatch(
    endPoints.class(id)
  );
  const handleChange = ({
    fileList: newFileList,
  }: {
    fileList: UploadFile[];
  }) => {
    setFileList(newFileList);
  };

  const handleUpload = () => {
    if (fileList.length > 0 && fileList[0].originFileObj) {
      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj);
      uploadImage(formData, {
        onSuccess: (response) => {
          setUploadId(response.data.data.id);
        },
      });
    }
  };
  const [form] = Form.useForm();
  const handleDeselect = (value: number | string) => {
    const studentToRemove = classData?.data.data.students.find(
      (student) => student.user_id === value
    );
    if (studentToRemove) {
      setRemovedStudents((prev) => [
        ...prev,
        { value: studentToRemove.user_id, label: studentToRemove.name },
      ]);
    }
  };

  const handleSelect = (value: number | string) => {
    setRemovedStudents((prev) =>
      prev.filter((student) => student.value !== value)
    );
  };

  useEffect(() => {
    // If year_range is in "YYYY-YYYY" format, split it and convert to Dayjs objects
    const yearRange = classData?.data.data.year_range;
    const yearValues = yearRange
      ? yearRange.split('-').map((year) => dayjs(year))
      : undefined;

    form.setFieldsValue({
      name: classData?.data.data.name,
      year: yearValues,
      description: classData?.data.data.description,
      user_ids: classData?.data.data.students?.map((student) => ({
        value: student.user_id,
        label: student.name,
      })),
    });
  }, [classData, form]);

  const handleSubmit = (values: {
    name: string;
    year: [dayjs.Dayjs, dayjs.Dayjs];
    description: string;
    user_ids: { value: number; label: string }[];
  }) => {
    const originalUserIds =
      classData?.data.data.students?.map((student) => student.user_id) || [];
    const hasUserIdsChanged =
      JSON.stringify(originalUserIds) !==
      JSON.stringify(values.user_ids.map((student) => student.value));

    // Format year range as "YYYY-YYYY"
    const formattedYearRange = `${values.year[0].format('YYYY')}-${values.year[1].format('YYYY')}`;

    updateClass(
      {
        name: values.name,
        year_range: formattedYearRange,
        description: values.description,
        ...(hasUserIdsChanged && { user_ids: values.user_ids }),
        ...(uploadId && { banner_id: uploadId }),
        ...(removedStudents.length > 0 && {
          deleted_user_ids: removedStudents.map((student) => student.value),
        }),
      },
      {
        onSuccess: () => {
          message.success(translate('edit-class.success'));
          navigate('/classes');
        },
        onError: (error) => {
          message.error(error.message);
        },
      }
    );
  };
  return (
    <Edit
      isLoading={false}
      title={
        <div className="flex items-center gap-3">
          <BookOutlined />
          <h1 className="text-2xl font-bold">
            {translate('edit-class.title')}
          </h1>
        </div>
      }
      deleteButtonProps={{
        hidden: true,
      }}
      headerButtons={({ listButtonProps }) => (
        <>
          {listButtonProps && (
            <Space>
              <ListButton {...listButtonProps}>
                {translate('buttons.list')}
              </ListButton>
              <RefreshButton />
            </Space>
          )}
        </>
      )}
      footerButtons={
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={updateClassLoading}
        >
          {translate('create-class.save')}
        </Button>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label={translate('listing-classes.name')}
          name="name"
          rules={[
            {
              required: true,
              message: translate('edit-class.validation.name'),
            },
          ]}
        >
          <Input placeholder={translate('edit-class.placeholder.name')} />
        </Form.Item>

        <Form.Item
          label={translate('listing-classes.year')}
          name="year"
          rules={[
            {
              required: true,
              message: translate('edit-class.placeholder.year'),
            },
          ]}
          className="w-full"
        >
          <DatePicker.RangePicker
            picker="year"
            className="w-full"
            placeholder={[
              translate('edit-class.placeholder.start-year'),
              translate('edit-class.placeholder.end-year'),
            ]}
          />
        </Form.Item>

        <Form.Item
          label={translate('listing-classes.description')}
          name="description"
          rules={[
            {
              required: true,
              message: translate('edit-class.validation.description'),
            },
          ]}
        >
          <Input.TextArea
            rows={4}
            placeholder={translate('listing-classes.description')}
          />
        </Form.Item>
        <Form.Item
          name="user_ids"
          label={translate('listing-classes.students')}
        >
          <Select
            mode="multiple"
            options={[
              ...(usersData?.data.data.map((user) => ({
                label: user.name,
                value: user.id,
              })) || []),
              ...removedStudents,
            ]}
            onDeselect={handleDeselect}
            onSelect={handleSelect}
            loading={usersLoading}
            placeholder={translate('listing-classes.select-students')}
          />
        </Form.Item>
        <Form.Item label={translate('listing-classes.banner')} name="banner">
          <div className="flex flex-wrap items-center gap-2">
            <Upload
              listType="picture"
              maxCount={1}
              fileList={fileList}
              disabled={uploadId !== null}
              onChange={handleChange}
              className="w-full"
              beforeUpload={() => false}
            >
              <Button icon={<UploadOutlined />} disabled={uploadId !== null}>
                {uploadId
                  ? translate('create-class.upload-success')
                  : translate('create-class.select-file')}
              </Button>
            </Upload>
            {!uploadId && (
              <Button
                type="primary"
                onClick={handleUpload}
                loading={uploadImageLoading}
                disabled={fileList.length === 0}
              >
                {translate('create-class.upload')}
              </Button>
            )}
          </div>
        </Form.Item>
      </Form>
    </Edit>
  );
};
